/* eslint-disable react-refresh/only-export-components */
import { useMutation, useQuery } from 'urql';
import { graphql } from '../gql/index.ts';
import { useForm, useWatch } from 'react-hook-form';
import { InputStateWrapper as HookedInput } from './form-components/InputStateWrapper.tsx';
import { MailSettings } from '../gql/graphql.ts';
import { Button } from '@progress/kendo-react-buttons';
import { Loader } from '@progress/kendo-react-indicators';
import { useEffect, useRef, useState } from 'react';
import { Label } from '@progress/kendo-react-labels';
import { PageTitle } from './PageTitle.tsx';
import { Fieldset } from './form-components/Fieldset.tsx';
import { NumericInputStateWrapper } from './form-components/NumericInputStateWrapper.tsx';
import { Input } from './form-components/Input.tsx';
import { HeroIcon } from '../shared/HeroIcon.tsx';
import { Link } from '@tanstack/react-router';
import { Grid, GridColumn, GridNoRecords } from '@progress/kendo-react-grid';
import { CheckboxStateWrapper } from './form-components/CheckboxStateWrapper.tsx';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { FieldWrapper } from '@progress/kendo-react-form';
import { cloneAndRemoveTypename } from '../public/util.ts';

interface TemplateName {
  templateName: string;
  slug: string;
  inputName?: keyof MailSettings | 'sendSignUpCanceledMail';
  description?: string;
  hidden?: boolean;
}

export const templateNames: TemplateName[] = [
  {
    templateName: 'Bestätigung Seminaranmeldung',
    slug: 'signup-confirmation',
    inputName: 'sendConfirmationMail',
    description:
      'Nach einer Anmeldung bei einem Seminar bekommt der Teilnehmer eine E-Mail.',
  },
  {
    templateName: 'Bestätigung Seminaranmeldung Warteliste',
    slug: 'signup-confirmation-waitlist',
    inputName: 'sendConfirmationMailWaitlist',
    description:
      'Nach einer Anmeldung in der Warteliste bei einem Seminar bekommt der Teilnehmer eine E-Mail.',
  },
  {
    templateName: 'Erinnerungsmail 7 Tage',
    slug: 'reminder-seven-days',
    inputName: 'sendReminderSevenDays',
    description:
      'Sieben Tage vor Beginn des Seminars wird der Teilnehmer an das Seminar erinnert. Diese Einstellung kann jeder Teilnehmer individuell deaktivieren.',
  },
  {
    templateName: 'Erinnerungsmail Vortag',
    slug: 'reminder-one-day',
    inputName: 'sendReminderOneDay',
    description:
      'Einen Tag vor Beginn des Seminars wird der Teilnehmer an das Seminar erinnert. Diese Einstellung kann jeder Teilnehmer individuell deaktivieren.',
  },
  {
    templateName: 'Erinnerungsmail 7 Tage Dozent',
    slug: 'reminder-seven-days-lecturer',
    inputName: 'sendReminderSevenDaysForLecturer',
    description:
      'Sieben Tage vor Beginn des Seminars wird der Dozent an das Seminar erinnert. Diese Einstellung kann jeder Dozent individuell deaktivieren.',
  },
  {
    templateName: 'Erinnerungsmail Vortag Dozent',
    slug: 'reminder-one-day-lecturer',
    inputName: 'sendReminderOneDayForLecturer',
    description:
      'Eine Erinnerungsmail, die einen Tag vor dem Seminar an den Dozenten versendet wird.',
  },
  {
    templateName: 'Bestätigung Abmeldung',
    slug: 'signup-canceled',
    inputName: 'sendSignUpCanceledMail',
    hidden: true,
    description:
      'Diese E-Mail wird ausgelöst, wenn die Abmeldungs-Funktion aktivert ist und sich ein Teilnehmer von einem Seminar abmeldet. Sie wird an einen spezifischen Empfänger verschickt (z.B. das Seminarhaus), der weiter oben definiert werden kann. Hier kann das Template editiert werden (Ein- und Ausschalten erfolgt in den "Allgemein"-Einstellungen unter "Funktionalität").',
  },
];

interface MailSettingsPageProps {
  companyId: string;
}

const MailSettingsQuery = graphql(/* GraphQL */ `
  query company($companyId: String!) {
    company(companyId: $companyId) {
      mailSettings {
        smtpHost
        smtpPort
        smtpUser
        smtpPassword
        smtpTls
        smtpSsl
        senderName
        senderMail
        allMailsSendToBcc
        receiverSignUpCanceled
        sendConfirmationMail
        sendConfirmationMailWaitlist
        sendReminderSevenDays
        sendReminderOneDay
        sendReminderSevenDaysForLecturer
        sendReminderOneDayForLecturer
      }
      functionalSettings {
        cancelSignUpActive
      }
    }
  }
`);

const SendTestMailMutation = graphql(/* GraphQL */ `
  mutation sendTestMail($companyId: String!, $receiver: String!) {
    sendTestMail(companyId: $companyId, receiver: $receiver)
  }
`);

const UpdateCompanyMutation = graphql(/* GraphQL */ `
  mutation updateCompany($companyId: String!, $company: UpdateCompanyDto!) {
    updateCompany(companyId: $companyId, company: $company) {
      id
      mailSettings {
        smtpHost
        smtpPort
        smtpUser
        smtpPassword
        smtpTls
        smtpSsl
        senderName
        senderMail
        allMailsSendToBcc
        receiverSignUpCanceled
        sendConfirmationMail
        sendConfirmationMailWaitlist
        sendReminderSevenDays
        sendReminderOneDay
        sendReminderSevenDaysForLecturer
        sendReminderOneDayForLecturer
      }
    }
  }
`);

export const MailSettingsPage: React.FC<MailSettingsPageProps> = ({
  companyId,
}) => {
  // TODO: Error handling
  const [{ data, fetching }] = useQuery({
    query: MailSettingsQuery,
    variables: { companyId },
    pause: companyId === undefined || companyId === null,
  });

  const [, updateCompany] = useMutation(UpdateCompanyMutation);
  const [, sendTestMail] = useMutation(SendTestMailMutation);

  const testMailInputValueRef = useRef<HTMLInputElement>(null);

  const [testMailState, setTestMailState] = useState<
    'idle' | 'sending' | 'success' | 'error'
  >('idle');
  const testMailCooldownTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (testMailCooldownTimerRef.current) {
        window.clearTimeout(testMailCooldownTimerRef.current);
      }
    };
  }, []);

  const handleSendTestMail = () => {
    if (!testMailInputValueRef.current?.value) return;
    if (testMailState !== 'idle') return;
    setTestMailState('sending');
    sendTestMail({
      companyId,
      receiver: testMailInputValueRef.current.value,
    }).then((r) => {
      const success = !r.error && r.data?.sendTestMail !== false;
      setTestMailState(success ? 'success' : 'error');
      if (testMailCooldownTimerRef.current) {
        window.clearTimeout(testMailCooldownTimerRef.current);
      }
      testMailCooldownTimerRef.current = window.setTimeout(
        () => setTestMailState('idle'),
        success ? 5000 : 3000,
      );
    });
  };

  const { control, formState, handleSubmit, reset, setValue } =
    useForm<MailSettings>({
      mode: 'onChange',
      reValidateMode: 'onChange',
    });

  const onFormSubmit = (values: MailSettings) => {
    updateCompany({
      companyId,
      company: { mailSettings: values },
    }).then((r) => {
      const withoutTypename =
        r.data?.updateCompany?.mailSettings &&
        cloneAndRemoveTypename(r.data?.updateCompany?.mailSettings);
      // TODO: Check for errors and indicate success/failure to UI
      reset(withoutTypename);
    });
  };

  useEffect(() => {
    if (!fetching) {
      const withoutTypename =
        data?.company?.mailSettings &&
        cloneAndRemoveTypename(data?.company?.mailSettings);
      reset(withoutTypename);
    }
  }, [fetching]);

  const smtpSsl = useWatch({ control, name: 'smtpSsl' });
  const smtpTls = useWatch({ control, name: 'smtpTls' });
  const encryptionValue = smtpSsl ? 'ssl' : smtpTls ? 'starttls' : 'none';

  const { isValid, isDirty } = formState;

  return (
    <>
      <PageTitle title="E-Mail Einstellungen" />

      <form
        onSubmit={handleSubmit(onFormSubmit)}
        className="k-form k-form-md relative flex w-full flex-col rounded-md bg-white shadow lg:w-full"
      >
        <Fieldset
          legend="SMTP-Konfiguration"
          description="Das System verschickt alle automatisierten E-Mails über die angegebenen Konfigurationsdaten."
        >
          <div className="flex flex-col gap-4 md:col-span-9 [&_.k-form-field]:!mt-0">
            <HookedInput
              name={'senderName'}
              label={'Absendername'}
              placeholder="Lisa Musterfrau"
              hint={'Dieser Name taucht beim Empfänger als Absender auf.'}
              rules={{ required: 'Dieses Feld muss ausgefüllt werden!' }}
              control={control}
            />
            <HookedInput
              name={'senderMail'}
              label={'Absender E-Mail'}
              placeholder="lisa@musterfrau.de"
              hint={'Diese Adresse taucht beim Empfänger als Absender auf.'}
              rules={{ required: 'Dieses Feld muss ausgefüllt werden!' }}
              control={control}
            />
            <div className="flex gap-4">
              <HookedInput
                name={'smtpHost'}
                label={'Serveradresse'}
                placeholder="smtp.musterfrau.de"
                hint={'Adresse des SMTP-Servers.'}
                rules={{ required: 'Dieses Feld muss ausgefüllt werden!' }}
                wrapperClassName="flex-1"
                control={control}
              />
              <div className="w-72">
                <NumericInputStateWrapper
                  name={'smtpPort'}
                  label={'Port'}
                  placeholder="587"
                  hint="Üblicherweise 25, 587 oder 465"
                  rules={{
                    required: 'Dieses Feld muss ausgefüllt werden!',
                    min: {
                      value: 0,
                      message: 'Wert muss zwischen 0 und 65536 liegen',
                    },
                    max: {
                      value: 65536,
                      message: 'Wert muss zwischen 0 und 65536 liegen',
                    },
                  }}
                  control={control}
                />
              </div>
            </div>
            <div className="flex gap-4">
              <HookedInput
                name={'smtpUser'}
                label={'Benutzername'}
                placeholder="lisa-musterfrau"
                rules={{ required: 'Dieses Feld muss ausgefüllt werden!' }}
                wrapperClassName="flex-1"
                control={control}
              />
              <FieldWrapper className="self-start">
                <Label className="k-form-label !mb-1">Verschlüsselung</Label>
                <div className="k-form-field-wrap w-72">
                  <DropDownList
                    className="[&.k-picker]:!border-[#ced4da]"
                    textField="text"
                    dataItemKey="value"
                    data={[
                      { text: 'SSL', value: 'ssl' },
                      { text: 'StartTLS', value: 'starttls' },
                      { text: 'Keine', value: 'none' },
                    ]}
                    value={
                      [
                        { text: 'SSL', value: 'ssl' },
                        { text: 'StartTLS', value: 'starttls' },
                        { text: 'Keine', value: 'none' },
                      ].find((item) => item.value === encryptionValue) ?? null
                    }
                    onChange={(e) => {
                      const v = e.target.value.value;
                      setValue('smtpSsl', v === 'ssl', {
                        shouldDirty: true,
                      });
                      setValue('smtpTls', v === 'starttls', {
                        shouldDirty: true,
                      });
                    }}
                  />
                </div>
              </FieldWrapper>
            </div>
            <HookedInput
              name={'smtpPassword'}
              label={'Passwort'}
              type="password"
              placeholder="abc123"
              rules={{ required: 'Dieses Feld muss ausgefüllt werden!' }}
              control={control}
            />
            <div>
              <Label className="k-form-label">Testmail versenden an</Label>
              <div className="flex gap-4">
                <Input
                  id="testMailReceiver"
                  name={'testMailReceiver'}
                  placeholder="max@mustermann.de"
                  wrapperClassName="flex-1"
                  ref={testMailInputValueRef}
                />
                <Button
                  type="button"
                  title={
                    testMailState === 'success'
                      ? 'Testmail wurde gesendet'
                      : testMailState === 'error'
                        ? 'Fehler beim Senden der Testmail'
                        : 'Testmail versenden'
                  }
                  themeColor={
                    testMailState === 'success'
                      ? 'success'
                      : testMailState === 'error'
                        ? 'error'
                        : undefined
                  }
                  disabled={testMailState !== 'idle'}
                  className="[&>span.k-button-text]:flex [&>span.k-button-text]:items-center [&>span.k-button-text]:gap-1"
                  onClick={handleSendTestMail}
                >
                  <span className="flex h-5 w-5 items-center justify-center">
                    {testMailState === 'sending' ? (
                      <Loader type="pulsing" size="small" />
                    ) : testMailState === 'success' ? (
                      <HeroIcon name="CheckCircle" className="block h-5 w-5" />
                    ) : testMailState === 'error' ? (
                      <HeroIcon name="XCircle" className="block h-5 w-5" />
                    ) : (
                      <HeroIcon
                        name="PaperAirplane"
                        className="block h-5 w-5"
                      />
                    )}
                  </span>
                  <div className="min-w-[5rem] text-center">
                    {testMailState === 'sending'
                      ? 'Sendet...'
                      : testMailState === 'success'
                        ? 'Gesendet'
                        : testMailState === 'error'
                          ? 'Fehler'
                          : 'Testen'}
                  </div>
                </Button>
              </div>
            </div>
          </div>
        </Fieldset>

        <Fieldset
          legend="Empfänger"
          description="Zusätzliche Empfänger für automatisierte E-Mails."
        >
          <div className="flex flex-col gap-4 md:col-span-9 [&_.k-form-field]:!mt-0">
            <HookedInput
              name={'allMailsSendToBcc'}
              label={'BCC-Empfänger für alle E-Mails'}
              hint={
                'Alle E-Mails werden zusätzlich an diese E-Mail verschickt.'
              }
              control={control}
            />
            <HookedInput
              name={'receiverSignUpCanceled'}
              label={'Empfänger für Abmeldungen'}
              hint={
                'Wenn sich ein Teilnehmer vor dem Seminarbeginn abmeldet, wird eine Mail an diese Adresse geschickt.'
              }
              control={control}
              required={data?.company?.functionalSettings.cancelSignUpActive}
            />

            {/* <HookedInput
              name={'receiverNotifications'}
              label={'Empfänger für Notifications'}
              control={control}
            /> */}
            {/* <CheckboxStateWrapper
              name={'sendNotificationAtRegistration'}
              label={'Benachrichtigung bei neuer Anmeldung'}
              control={control}
            />
            <CheckboxStateWrapper
              name={'sendNotificationOnUpdateSeminar'}
              label={'Benachrichtigung bei Seminaraktualisierung'}
              control={control}
            /> */}
          </div>
        </Fieldset>
        <Fieldset
          legend={`Vorlagen`}
          description="Aktivieren Sie einzelne Benachrichtigungen oder bearbeiten Sie die E-Mail-Vorlagen für alle E-Mails die das System versendet."
        >
          <div className="flex flex-col gap-4 md:col-span-9 [&_.k-form-field]:!mt-0">
            <style>
              {`
              .templateGrid .k-grid-header col:nth-of-type(1){
                width: 70%
              }
              .templateGrid .k-grid-table col:nth-of-type(1){
                width: 70%
              }
              .templateGrid .k-grid-header col:nth-of-type(2){
                width: 10%
              }
              .templateGrid .k-grid-table col:nth-of-type(2){
                width: 10%
              }
              .templateGrid .k-grid-header col:nth-of-type(3){
                width: 20%
              }
              .templateGrid .k-grid-table col:nth-of-type(3){
                width: 20%
              }`}
            </style>
            <Grid
              className="templateGrid h-auto w-full [&_.k-grid-norecords-template]:border-0"
              scrollable={'scrollable'}
              data={templateNames}
            >
              <GridNoRecords>
                {fetching ? ' ' : 'Keine Vorlage vorhanden'}
              </GridNoRecords>
              <GridColumn
                title="Vorlage"
                cells={{
                  data: (props) => (
                    <td
                      {...props.tdProps}
                      className={`!flex !flex-col gap-2 ${props.tdProps?.className}`}
                    >
                      <span className="text-lg">
                        {props.dataItem.templateName}
                      </span>
                      <br />
                      <span className="text-sm">
                        {props.dataItem.description}
                      </span>
                    </td>
                  ),
                }}
              />
              <GridColumn
                title="Aktiviert"
                cells={{
                  data: (props) => (
                    <td {...props.tdProps}>
                      {!props.dataItem.hidden && (
                        <CheckboxStateWrapper
                          key={props.dataItem.inputName}
                          name={
                            (props.dataItem.inputName as keyof MailSettings) ||
                            ''
                          }
                          control={control}
                        />
                      )}
                    </td>
                  ),
                }}
              />
              <GridColumn
                title="Aktionen"
                cells={{
                  data: (props) => (
                    <td className={'k-table-td'}>
                      <div className="k-button-link-secondary flex gap-1">
                        <Link
                          // Slug comes from data and resolves to one of 7 static
                          // routes registered under /admin/mailsettings/templates.
                          to={
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            `/admin/mailsettings/templates/${props.dataItem.slug}` as any
                          }
                          className="k-button k-button-md k-rounded-md k-button-outline k-button-outline-secondary"
                          title="Vorlage bearbeiten"
                        >
                          <HeroIcon name="Pencil" className="block h-5 w-5" />
                        </Link>
                      </div>
                    </td>
                  ),
                }}
              />
            </Grid>
          </div>
        </Fieldset>
        <div className="k-form-buttons !mt-0 justify-end rounded-md bg-gray-50 !px-8 !py-3 !text-right">
          <Button
            type={'submit'}
            themeColor={'primary'}
            className="!shadow-sm [&>span.k-button-text]:flex [&>span.k-button-text]:items-center [&>span.k-button-text]:gap-1"
            disabled={!isValid || !isDirty}
            title="Speichern"
          >
            <HeroIcon name="CheckCircle" className="block h-5 w-5" />
            <div>Speichern</div>
          </Button>
        </div>
      </form>
    </>
  );
};
